// filepath: apps/web/src/features/proc/useCancelInstance.ts
//
// FR-UI-PROC-1 AC-5 — TanStack Query mutation hook for
// POST /api/processes/instances/{id}/cancel.
//
// Mirrors useRetryStep.ts and the FR-UI-PROM-2 mutation pattern
// (useApprovePromotion.ts + useRejectPromotion.ts):
//   - generates an idempotency_key via `crypto.randomUUID()` per
//     ARCH Rule 3;
//   - sends the body as `{idempotency_key}` only (INV-5);
//   - invalidates `procKeys.failedInstances` on success so the
//     dashboard row transitions out of `failed` automatically;
//   - 204 No Content on success (per internal/obs/handler.go:223-262).
//
// Confirm: the cancel action is destructive (terminates the instance
// state machine) — the row component renders a confirmation dialog
// before invoking `mutateAsync`. The mutation hook itself does NOT
// enforce the confirmation; the UI layer is responsible.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/definitions/types";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import { procKeys } from "./types";

interface CancelInstanceVariables {
  instanceId: string;
}

export interface UseCancelInstanceOptions {
  onError?: (err: ApiError) => void;
  onSuccess?: () => void;
}

export interface UseCancelInstanceReturn {
  mutate: (variables: CancelInstanceVariables) => void;
  mutateAsync: (
    variables: CancelInstanceVariables,
  ) => Promise<void | undefined>;
  isPending: boolean;
  isError: boolean;
  error: ApiError | null;
}

export function useCancelInstance(
  opts: UseCancelInstanceOptions = {},
  apiBase = "/api",
): UseCancelInstanceReturn {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();

  const mutation = useMutation<void, ApiError, CancelInstanceVariables>({
    mutationFn: async ({ instanceId }: CancelInstanceVariables) => {
      const url = `${apiBase}/processes/instances/${encodeURIComponent(
        instanceId,
      )}/cancel`;
      // ARCH Rule 3 — generate idempotency_key client-side via
      // crypto.randomUUID(). Mirrors useApprovePromotion.ts:25.
      const idempotencyKey = crypto.randomUUID();

      let response: Response;
      try {
        // ISS-377 round 3 R2-B2.5: migrated to apiFetch. The
        // body field `idempotency_key` is part of the server
        // contract (ARCH Rule 3); the apiFetch `idempotencyKey`
        // flag is intentionally NOT passed (the action
        // handler reads the body field, not the X-Idempotency-Key
        // header) so a redundant header is not attached.
        response = await apiFetch(url, {
          method: "POST",
          init: {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ idempotency_key: idempotencyKey }),
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
        );
      }

      // 204 No Content — no body to parse.
      return undefined;
    },

    onSuccess: (_void, variables) => {
      void queryClient.invalidateQueries({
        queryKey: procKeys.failedInstances(tenantSlug),
      });
      void queryClient.invalidateQueries({
        queryKey: procKeys.stepLog(tenantSlug, variables.instanceId),
      });
      opts.onSuccess?.();
    },

    onError: (err) => {
      opts.onError?.(err);
    },

    retry: (count, err) => {
      // Never auto-retry 4xx — caller decides whether to retry.
      if (err instanceof ApiError) {
        if (err.status >= 400 && err.status < 500) return false;
      }
      return count < 2;
    },
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}
